import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "@resideo/blueprint-oneportal";
import { externalLinkDarkIcon } from "@/assets/icons";
import styles from "./GlobalHeader.module.css";

export interface GlobalHeaderOption {
  value: string;
  label: string;
}

export interface GlobalHeaderProps {
  contextOptions: GlobalHeaderOption[];
  selectedContext: string;
  onContextChange: (value: string) => void;
  /** The company switcher is only rendered when this is provided. */
  companyOptions?: GlobalHeaderOption[];
  selectedCompany?: string;
  onCompanyChange?: (value: string) => void;
  userName?: string;
  /** Hides the name + avatar trigger and its flyout (e.g. before sign-in completes). */
  showProfile?: boolean;
  onMyProfile: () => void;
  onManageResideoId: () => void;
  onSignOut: () => void;
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  contextOptions,
  selectedContext,
  onContextChange,
  companyOptions,
  selectedCompany,
  onCompanyChange,
  userName = "",
  showProfile = true,
  onMyProfile,
  onManageResideoId,
  onSignOut,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isMenuOpen) {
      flyoutRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  const runAndClose = (action: () => void) => () => {
    setIsMenuOpen(false);
    action();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setIsMenuOpen(true);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
    const current = items.indexOf(document.activeElement as HTMLElement);
    const lastIndex = items.length - 1;
    let next: number;

    switch (event.key) {
      case "ArrowDown":
        next = current >= lastIndex ? 0 : current + 1;
        break;
      case "ArrowUp":
        next = current <= 0 ? lastIndex : current - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = lastIndex;
        break;
      case "Tab":
        event.preventDefault();
        setIsMenuOpen(false);
        triggerRef.current?.focus();
        return;
      default:
        return;
    }

    event.preventDefault();
    items[next]?.focus();
  };

  const firstName = userName.split(" ").filter(Boolean)[0] ?? "";

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <div className={styles.brandLogo}>{t("layout.brandName")}</div>
        <div className={styles.brandDivider} />
        <Select
          name="context-select"
          className={styles.headerSelect}
          value={selectedContext}
          items={contextOptions}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value;
            if (value && value !== selectedContext) onContextChange(value);
          }}
        />
        {companyOptions && onCompanyChange && (
          <Select
            name="company-select"
            className={`${styles.headerSelect} ${styles.companySelect}`}
            value={selectedCompany ?? ""}
            items={companyOptions}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const value = e.target.value;
              if (value && value !== selectedCompany) onCompanyChange(value);
            }}
          />
        )}
      </div>

      {showProfile && (
        <div className={styles.userMenu} ref={menuRef}>
          <button
            ref={triggerRef}
            type="button"
            className={styles.userTrigger}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            onKeyDown={handleTriggerKeyDown}
            aria-label={t("layout.accountMenu")}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
          >
            <span className={styles.userName}>{userName || t("layout.user")}</span>
            <span className={styles.userAvatar}>{getInitials(userName) || "?"}</span>
          </button>
          {isMenuOpen && (
            <div
              ref={flyoutRef}
              className={styles.flyout}
              role="menu"
              onKeyDown={handleMenuKeyDown}
            >
              <p className={styles.flyoutGreeting}>
                {t("layout.greeting", { name: firstName })}
              </p>
              <div className={styles.flyoutDivider} role="separator" />
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={styles.flyoutLink}
                onClick={runAndClose(onMyProfile)}
              >
                <span className={styles.flyoutLinkLabel}>{t("layout.myProfile")}</span>
              </button>
              <div className={styles.flyoutDivider} role="separator" />
              <div className={styles.flyoutSection}>
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={`${styles.flyoutLink} ${styles.flyoutLinkRow}`}
                  onClick={runAndClose(onManageResideoId)}
                >
                  <span className={styles.flyoutLinkLabel}>
                    {t("layout.manageResideoId")}
                  </span>
                  <img
                    src={externalLinkDarkIcon}
                    width="16"
                    height="16"
                    alt=""
                    aria-hidden="true"
                  />
                </button>
                <p className={styles.flyoutDescription}>
                  {t("layout.manageResideoIdDescription")}
                </p>
              </div>
              <div className={styles.flyoutDivider} role="separator" />
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={styles.flyoutLink}
                onClick={runAndClose(onSignOut)}
              >
                <span className={styles.flyoutLinkLabel}>{t("layout.signOut")}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
